import { CircularProgress } from "@material-ui/core";
import { Autocomplete as MuiAutocomplete } from "@material-ui/lab";
import { ListItemDto } from "Api/Api";
import { SearchIcon } from "Components/Shared/Icons/SearchIcon";
import { TrashCanIcon } from "Components/Shared/Icons/TrashCanIcon";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import useDebounce from "Hooks/useDebounce";
import * as React from "react";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";
import { FieldErrorProps, useFieldError } from "Hooks/useFieldError";
import { StyledInputLabel } from "Components/Shared/StyledComponents";

export type AutocompleteState = {
  options: ListItemDto[];
  selectedItem: ListItemDto | null;
  isLoading: boolean;
};

type Props = FieldErrorProps & {
  value?: ListItemDto | null;
  defaultValue?: ListItemDto | null;
  onSearch: (searchString: string) => void;
  options: ListItemDto[];
  onChange: (option: ListItemDto | null) => void;
  isLoading: boolean;
  placeholder?: string;
  debounceTime?: number;
  label?: string;
};

const StyledAutocomplete = styled.div`
  .ibis-MuiAutocomplete-inputRoot {
    .ibis-MuiInputBase-input {
      padding-top: 0;
      padding-bottom: 0;
      padding-left: 0 !important;
    }
  }
  .ibis-MuiAutocomplete-popupIndicatorOpen {
    transform: none;
  }

  .ibis-MuiAutocomplete-endAdornment {
    top: 5px;
    .ibis-MuiButtonBase-root {
      width: 25px;
      height: 25px;
    }
  }

  .ibis-MuiAutocomplete-clearIndicator {
    margin-right: ${props => props.theme.spacing(1)}px;
  }
`;

const StyledPopupIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const StyledLoadingIcon = styled(CircularProgress)`
  width: 20px;
  height: 20px;
  position: absolute;
  right: 40px;
`;

export const Autocomplete: React.FunctionComponent<Props> = props => {
  const { hasError, originalProps } = useFieldError(props);
  const {
    placeholder,
    onSearch,
    debounceTime,
    onChange,
    label,
    value,
    defaultValue,
  } = originalProps;
  const { t } = useResource();

  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(props.isLoading);
  const [options, setOptions] = React.useState(props.options);

  const [searchTerm, setSearchTerm] = React.useState("");
  const [lastSearchTerm, setLastSearchTerm] = React.useState("");
  const debouncedSearchTerm = useDebounce(
    searchTerm,
    debounceTime ?? 500,
  ) as string;

  React.useEffect(() => {
    if (debouncedSearchTerm !== lastSearchTerm) {
      onSearch(debouncedSearchTerm);
      setLastSearchTerm(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onSearch, lastSearchTerm, setLastSearchTerm]);

  React.useEffect(() => {
    setLoading(props.isLoading);
  }, [props.isLoading]);

  React.useEffect(() => {
    setOptions(props.options);
  }, [props.options]);

  return (
    <StyledAutocomplete>
      {label && (
        <StyledInputLabel hasError={hasError} className={"form-label"}>
          {label}
        </StyledInputLabel>
      )}
      <MuiAutocomplete
        value={value}
        defaultValue={defaultValue}
        open={open}
        onOpen={() => {
          setOpen(true);
          onSearch(debouncedSearchTerm);
          setLoading(true);
        }}
        onClose={() => {
          setOpen(false);
          setSearchTerm("");
        }}
        onChange={(_, option) => {
          onChange(option);
        }}
        getOptionSelected={(option, val) => option.itemID === val.itemID}
        getOptionLabel={option => option.name}
        options={options}
        loading={loading}
        noOptionsText={t(Resources.Common.Autocomplete.NoOptions)}
        loadingText={t(Resources.Common.Autocomplete.Loading)}
        clearText={t(Resources.Common.Autocomplete.Clear)}
        openText={t(Resources.Common.Autocomplete.Open)}
        closeText={t(Resources.Common.Autocomplete.Close)}
        filterOptions={undefined}
        popupIcon={
          <StyledPopupIcon>
            <SearchIcon></SearchIcon>
          </StyledPopupIcon>
        }
        closeIcon={
          <>
            {!loading && (
              <StyledPopupIcon>
                <TrashCanIcon />
              </StyledPopupIcon>
            )}
          </>
        }
        renderInput={params => (
          <TextInput
            {...params}
            placeholder={placeholder}
            fieldError={props.fieldError}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <StyledLoadingIcon color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            onChange={(e: {
              target: { value: React.SetStateAction<string> };
            }) => {
              setSearchTerm(e.target.value);
              setLoading(true);
              setOptions([]);
            }}
          />
        )}
      />
    </StyledAutocomplete>
  );
};
